import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { Theme } from 'helpers/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    borderRadius: theme.buttonBorderRadius,
    padding: [0, theme.spacing(2)],
    border: '1px solid',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    height: 40,
    justifyContent: 'center',
    fontSize: theme.fontSizes.default,
    transition: '0.1s ease',
    transitionProperty: 'border-colour, background-color',
    outline: 'none',
    gap: '10px',
    // it would be nice to have a light blue hover state
    '&:focus': {
      borderColor: theme.colors.primaryBlue
    },
    '&::-moz-focus-inner': {
      border: 0
    }
  },
  bold: {
    fontWeight: 'bold !important'
  },
  small: {
    padding: [0, theme.spacing(1)]
  },
  default: {
    color: theme.colors.primaryBlack,
    borderColor: theme.colors.borderGrey,
    backgroundColor: 'white',
    fontWeight: 200,
    borderRadius: 4
  },
  grey: {
    color: theme.colors.primaryBlack,
    borderColor: theme.colors.borderGrey,
    backgroundColor: theme.colors.backgroundGrey
  },
  submit: {
    color: 'white',
    border: '1px solid #2550C1',
    backgroundColor: theme.colors.primaryBlue,
    borderRadius: 6,
    fontWeight: 800,
    textWrap: 'nowrap',
    boxShadow:
      '0px -2.46203px 4.10338px 0px rgba(3, 3, 3, 0.10) inset, 0px 1.64135px 4px 0px rgb(0 0 0 / 19%)',
    '&:focus': {
      borderColor: '#0044db',
      backgroundColor: 'background-color: #0950ee;'
    },
    '&:hover': {
      backgroundColor: '#0a4cdf'
    }
  },
  mainGrey: {
    color: theme.colors.primaryBlack,
    textWrap: 'nowrap',
    border: '1px solid #E0E5E8',
    background: '#FFF',
    borderRadius: 6,
    fontWeight: 600,
    boxShadow:
      '0px -2.46203px 4.10338px 0px rgba(3, 3, 3, 0.04) inset, 0px 1.64135px 1.64135px 0px rgba(0, 0, 0, 0.05)',

    '&:focus': {
      borderColor: '#0044db',
      backgroundColor: '#0044db'
    },
    '&:hover': {
      backgroundColor: '#f9f8fb'
    }
  },
  gradient: {
    color: 'white',
    backgroundImage: `linear-gradient(45deg,
      ${theme.colors.primaryBlue}, ${theme.colors.primaryGreen})`,
    fontWeight: 800,
    fontSize: theme.fontSizes.large,
    borderRadius: 4,
    border: 'none'
  },
  flat: {
    color: '#0C152E',
    border: '1px solid #E0E5E8',
    textWrap: 'nowrap',
    background: '#FFF',
    boxShadow: '0px 1.64135px 1.64135px 0px rgba(0, 0, 0, 0.05)',
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 6,
    height: 36,
    padding: '0 14px',
    '&:focus': {
      borderColor: '#95a2bf',
      backgroundColor: '#f4f4f4'
    },
    '&:hover': {
      backgroundColor: '#f9f8fb'
    }
  },
  flatPrimary: {
    extend: 'flat',
    color: 'white',
    background: '#0b57ff',
    border: '1px solid #2550C1',
    '&:focus': {
      borderColor: '#0044db',
      backgroundColor: 'background-color: #0950ee;'
    },
    '&:hover': {
      backgroundColor: '#0a4cdf'
    }
  },
  disabled: {
    opacity: 0.5,
    cursor: 'initial !important'
  },
  active: {
    border: '1px solid #0b57ff',
    color: '#0b57ff'
  }
}));

export type Archetypes =
  | 'default'
  | 'grey'
  | 'submit'
  | 'mainGrey'
  | 'gradient'
  | 'flat'
  | 'flatPrimary';

interface Props {
  archetype?: Archetypes;
  iconSize?: number;
  bold?: boolean;
  small?: boolean;
  className?: string;
  active?: boolean;
  noIconPadding?: boolean;
  disabled?: Boolean;
  onClick?: (evt?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

function Button({
  archetype,
  iconSize,
  noIconPadding,
  bold,
  small,
  children,
  active,
  className = '',
  disabled,
  onClick,
  ...props
}: Props & React.PropsWithoutRef<JSX.IntrinsicElements['button']>) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  return (
    <button
      className={classNames(
        disabled && classes.disabled,
        classes.button,
        classes[archetype || 'default'],
        bold && classes.bold,
        small && classes.small,
        active && classes.active,
        className
      )}
      onClick={(evt) => {
        if (!disabled && onClick) onClick(evt);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

export default Button;
